import {
    buildMeasurePointAxisDisplayRows,
    meterCoordinateTupleToMillimeterTuple,
} from '../math/MeasureNumericKernel';
import type { MeasureCalloutViewState, MeasurePropertyRow } from '../types/measure-callout-types';
import {
    MeasureGeometryKind,
} from '../types/measure-property-types';
import {
    MeasureUnboundedGeometryKind,
    type MeasureUnboundedGeometryBinding,
} from '../types/measure-unbounded-types';
import type { MeasureEntityRef } from '../types/measure-types';

/**
 * 无边界测量属性解析器。
 *
 * 职责：为 datum-slot 与正式无限基准输出测量浮窗视图状态。
 */
export class MeasureUnboundedPropertyResolver {
    /**
     * 解析无边界测量实体浮窗状态。
     *
     * @param entityRef 测量实体引用。
     * @returns 浮窗视图状态；无法解析时返回 null。
     */
    public resolve(entityRef: MeasureEntityRef): MeasureCalloutViewState | null {
        const binding = entityRef.unboundedBinding;
        if (!binding) {
            return null;
        }

        const geometryKind = resolveUnboundedGeometryKind(binding);
        return {
            objectId: entityRef.objectId,
            entityName: binding.displayName,
            entityKindLabel: resolveUnboundedEntityKindLabel(geometryKind),
            worldPickPoint: entityRef.worldPickPoint.clone(),
            rows: buildUnboundedPropertyRows(geometryKind, entityRef),
        };
    }
}

/**
 * 将无边界绑定映射为测量几何类别。
 *
 * @param binding 无边界绑定。
 * @returns 测量几何类别。
 */
export function resolveUnboundedGeometryKind(
    binding: MeasureUnboundedGeometryBinding,
): MeasureGeometryKind {
    switch (binding.kind) {
        case MeasureUnboundedGeometryKind.GlbDatumSlotPlane:
        case MeasureUnboundedGeometryKind.FormalDatumPlane:
            return MeasureGeometryKind.InfiniteReferencePlane;
        case MeasureUnboundedGeometryKind.FormalDatumAxis:
            return MeasureGeometryKind.InfiniteReferenceLine;
        case MeasureUnboundedGeometryKind.GlbDatumSlotOrigin:
        case MeasureUnboundedGeometryKind.FormalDatumPoint:
            return MeasureGeometryKind.ReferencePoint;
        default:
            return MeasureGeometryKind.Unsupported;
    }
}

/**
 * 解析无边界实体类型标签。
 *
 * @param geometryKind 测量几何类别。
 * @returns 中文标签。
 */
function resolveUnboundedEntityKindLabel(geometryKind: MeasureGeometryKind): string {
    switch (geometryKind) {
        case MeasureGeometryKind.InfiniteReferencePlane:
            return '基准面（无限）';
        case MeasureGeometryKind.InfiniteReferenceLine:
            return '基准轴（无限）';
        case MeasureGeometryKind.ReferencePoint:
            return '点';
        default:
            return '几何体';
    }
}

/**
 * 构建无边界几何属性行。
 *
 * @param geometryKind 测量几何类别。
 * @param entityRef 测量实体引用。
 * @returns 属性行列表。
 */
function buildUnboundedPropertyRows(
    geometryKind: MeasureGeometryKind,
    entityRef: MeasureEntityRef,
): MeasurePropertyRow[] {
    switch (geometryKind) {
        case MeasureGeometryKind.InfiniteReferencePlane:
            return [
                { label: '范围', value: '无限延伸' },
            ];
        case MeasureGeometryKind.InfiniteReferenceLine:
            return [
                { label: '范围', value: '无限延伸' },
            ];
        case MeasureGeometryKind.ReferencePoint:
            return buildMeasurePointAxisDisplayRows(meterCoordinateTupleToMillimeterTuple([
                entityRef.worldPickPoint.x,
                entityRef.worldPickPoint.y,
                entityRef.worldPickPoint.z,
            ]));
        default:
            return [
                { label: '提示', value: '暂不支持该几何类型的测量' },
            ];
    }
}
